import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Sidebar from "@/components/Sidebar";
import AddTaskForm from "@/components/AddTaskForm";
import TaskItem from "@/components/TaskItem";
import EmptyState from "@/components/EmptyState";
import FocusMode from "@/components/FocusMode";
import EditTaskDialog from "@/components/EditTaskDialog";
import SettingsDialog from "@/components/SettingsDialog";
import ShareDialog from "@/components/ShareDialog";
import CreateListDialog from "@/components/CreateListDialog";
import DeleteListDialog from "@/components/DeleteListDialog";
import UpgradeModal from "@/components/UpgradeModal";
import MobileMenu from "@/components/MobileMenu";
import ShareListDialog from "@/components/ShareListDialog";
import ShareWorkspaceDialog from "@/components/ShareWorkspaceDialog";
import TopPicksBar from "@/components/TopPicksBar";
import AllTopPicks from "@/components/AllTopPicks";
import ListToolbar from "@/components/ListToolbar";
import TaskTimeline from "@/components/TaskTimeline";
import ExportDialog from "@/components/ExportDialog";
import { ShareAppLink } from "@/components/ShareApp";
import { useAuth } from "@/contexts/AuthContext";
import { registerServiceWorker, scheduleBriefingSW, cancelBriefingSW } from "@/lib/serviceWorker";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function isTyping() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [lists, setLists] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [removingIds, setRemovingIds] = useState(new Set());
  const [focusTaskId, setFocusTaskId] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [sharingTaskId, setSharingTaskId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [createListOpen, setCreateListOpen] = useState(false);
  const [deleteListTarget, setDeleteListTarget] = useState(null); // {list, taskCount}
  const [upgradeGate, setUpgradeGate] = useState(null); // {feature, message}
  const [billing, setBilling] = useState(null); // {plan, list_limit, list_count, days_remaining, grandfathered}
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sharingListId, setSharingListId] = useState(null);
  const [workspaceShareOpen, setWorkspaceShareOpen] = useState(false);
  const [exportingList, setExportingList] = useState(null);
  // Per-list sort/filter/view preferences (in-memory; reset on reload).
  const [listPrefs, setListPrefs] = useState({}); // {listId: {sort, priorityFilter, dueFilter, view}}
  const notifiedRef = useRef(new Set());
  const briefedDateRef = useRef(null);

  const fetchTasks = useCallback(async () => {
    try {
      const [activeRes, completedRes, listsRes, billingRes] = await Promise.all([
        axios.get(`${API}/tasks?status=active`),
        axios.get(`${API}/tasks?status=completed`),
        axios.get(`${API}/lists`),
        axios.get(`${API}/billing/status`).catch(() => ({ data: null })),
      ]);
      setTasks(activeRes.data);
      setCompletedTasks(completedRes.data);
      setLists(listsRes.data);
      if (billingRes.data) setBilling(billingRes.data);
    } catch {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Register service worker once and schedule briefing (works even when tab closes
  // on browsers that support the Notification Triggers API; falls back gracefully).
  useEffect(() => {
    registerServiceWorker();
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!user.notifications_enabled) {
      cancelBriefingSW();
      return;
    }
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    // Fetch latest top tasks then ask SW to schedule
    axios
      .get(`${API}/briefing/today`)
      .then((res) => {
        scheduleBriefingSW(user.briefing_time || "08:00", res.data?.top_tasks || []);
      })
      .catch(() => {});
  }, [user?.notifications_enabled, user?.briefing_time, user?.user_id, tasks.length]);

  useEffect(() => {
    if (!user?.notifications_enabled) return;
    const briefingTime = user.briefing_time || "08:00";
    const check = async () => {
      if (Notification.permission !== "granted") return;
      const now = new Date();
      const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(
        now.getMinutes()
      ).padStart(2, "0")}`;
      const dateKey = now.toDateString();
      if (hhmm !== briefingTime) return;
      if (briefedDateRef.current === dateKey) return;
      briefedDateRef.current = dateKey;
      try {
        const res = await axios.get(`${API}/briefing/today`);
        const top = res.data?.top_tasks || [];
        const body =
          top.length === 0
            ? "Clean slate — add your first task of the day!"
            : top.map((t, i) => `${i + 1}. ${t.title}`).join("\n");
        new Notification("Your daily briefing", { body });
      } catch {
        /* noop */
      }
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [user?.notifications_enabled, user?.briefing_time]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
    const check = () => {
      if (!("Notification" in window) || Notification.permission !== "granted") return;
      const now = new Date();
      tasks.forEach((t) => {
        if (t.completed || !t.due_date || notifiedRef.current.has(t.id)) return;
        const due = new Date(t.due_date);
        const diff = (due - now) / 60000;
        if (diff <= 60 && diff >= -1) {
          new Notification("Task due soon", { body: t.title });
          notifiedRef.current.add(t.id);
        }
      });
    };
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, [tasks]);

  const addTask = async (payload) => {
    try {
      const res = await axios.post(`${API}/tasks`, payload);
      setTasks((t) => [...t, res.data]);
      toast.success("Task added");
    } catch {
      toast.error("Failed to add task");
    }
  };

  const updateTask = async (id, updates) => {
    try {
      const res = await axios.patch(`${API}/tasks/${id}`, updates);
      setTasks((ts) => ts.map((t) => (t.id === id ? res.data : t)));
      return res.data;
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleTask = async (task) => {
    const newCompleted = !task.completed;
    if (newCompleted) {
      setRemovingIds((s) => new Set(s).add(task.id));
      setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, completed: true } : t)));
      await axios.patch(`${API}/tasks/${task.id}`, { completed: true }).catch(() => {});
      setTimeout(async () => {
        await fetchTasks();
        setRemovingIds((s) => {
          const ns = new Set(s);
          ns.delete(task.id);
          return ns;
        });
      }, 850);
    } else {
      // Restoring from completed view
      try {
        await axios.patch(`${API}/tasks/${task.id}`, { completed: false });
        await fetchTasks();
        toast.success("Task restored");
      } catch {
        toast.error("Failed");
      }
    }
  };

  const deleteTask = async (id) => {
    try {
      await axios.delete(`${API}/tasks/${id}`);
      setTasks((ts) => ts.filter((t) => t.id !== id));
      setCompletedTasks((ts) => ts.filter((t) => t.id !== id));
      toast.success("Task deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const addSubtask = async (taskId, title) => {
    try {
      const res = await axios.post(`${API}/tasks/${taskId}/subtasks`, { title });
      setTasks((ts) => ts.map((t) => (t.id === taskId ? res.data : t)));
    } catch {
      toast.error("Failed to add subtask");
    }
  };

  const toggleSubtask = async (taskId, subtaskId, completed) => {
    try {
      const res = await axios.patch(`${API}/tasks/${taskId}/subtasks/${subtaskId}`, { completed });
      setTasks((ts) => ts.map((t) => (t.id === taskId ? res.data : t)));
    } catch {
      toast.error("Failed");
    }
  };

  const deleteSubtask = async (taskId, subtaskId) => {
    try {
      const res = await axios.delete(`${API}/tasks/${taskId}/subtasks/${subtaskId}`);
      setTasks((ts) => ts.map((t) => (t.id === taskId ? res.data : t)));
    } catch {
      toast.error("Failed");
    }
  };

  const reorderSubtasks = async (taskId, subtaskIds) => {
    // Optimistic update
    setTasks((ts) =>
      ts.map((t) => {
        if (t.id !== taskId) return t;
        const byId = Object.fromEntries((t.subtasks || []).map((s) => [s.id, s]));
        return { ...t, subtasks: subtaskIds.map((sid) => byId[sid]).filter(Boolean) };
      })
    );
    try {
      await axios.post(`${API}/tasks/${taskId}/subtasks/reorder`, { subtask_ids: subtaskIds });
    } catch {
      toast.error("Subtask reorder failed");
    }
  };

  const clearArchive = async () => {
    if (!window.confirm(`Permanently delete all ${completedTasks.length} completed tasks?`)) return;
    try {
      const res = await axios.delete(`${API}/tasks/completed`);
      setCompletedTasks([]);
      toast.success(`Cleared ${res.data.deleted_count} completed task${res.data.deleted_count === 1 ? "" : "s"}`);
    } catch {
      toast.error("Failed to clear archive");
    }
  };

  const breakdown = async (taskId) => {
    const id = toast.loading("AI breaking down task...");
    try {
      const res = await axios.post(`${API}/tasks/${taskId}/breakdown`);
      setTasks((ts) => ts.map((t) => (t.id === taskId ? res.data : t)));
      toast.success("Subtasks generated", { id });
    } catch {
      toast.error("Breakdown failed", { id });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const visible = filteredTasks();
    const oldIdx = visible.findIndex((t) => t.id === active.id);
    const newIdx = visible.findIndex((t) => t.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(visible, oldIdx, newIdx);
    const otherTasks = tasks.filter((t) => !visible.includes(t));
    const merged = [...reordered, ...otherTasks].map((t, i) => ({ ...t, order: i }));
    setTasks(merged);
    try {
      await axios.post(`${API}/tasks/reorder`, {
        items: merged.map((t) => ({ id: t.id, order: t.order })),
      });
    } catch {
      toast.error("Reorder failed");
    }
  };

  // ---------- List management ----------
  const createList = async (payload) => {
    try {
      const { data } = await axios.post(`${API}/lists`, payload);
      setLists((prev) => [...prev, data].sort((a, b) => a.order - b.order));
      setFilter(`list:${data.id}`);
      setCreateListOpen(false);
      toast.success(`Created "${data.name}"`);
      // Refresh billing count
      axios.get(`${API}/billing/status`).then((r) => setBilling(r.data)).catch(() => {});
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && detail?.code === "upgrade_required") {
        setCreateListOpen(false);
        setUpgradeGate({ feature: detail.feature || "custom task lists", message: detail.message });
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Couldn't create list");
    }
  };

  // Pre-emptive gate for the sidebar "+ New list" button so Free users at the
  // cap see the upgrade modal immediately instead of opening then closing the
  // create dialog. Server-side enforcement remains authoritative.
  const handleRequestCreateList = () => {
    const limit = billing?.list_limit;
    const count = billing?.list_count ?? lists.length;
    if (limit != null && count >= limit) {
      setUpgradeGate({
        feature: "custom task lists",
        message: `Free plan is limited to ${limit} task lists. Upgrade to Pro for unlimited lists.`,
      });
      return;
    }
    setCreateListOpen(true);
  };

  const updateList = async (listId, payload) => {
    try {
      const { data } = await axios.patch(`${API}/lists/${listId}`, payload);
      setLists((prev) => prev.map((l) => (l.id === listId ? data : l)));
      // If category changed, re-fetch tasks to update their category badge.
      if ("category" in payload) await fetchTasks();
      toast.success("List updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update list");
    }
  };

  const requestDeleteList = (list) => {
    const taskCount = tasks.filter((t) => t.list_id === list.id).length
      + completedTasks.filter((t) => t.list_id === list.id).length;
    setDeleteListTarget({ list, taskCount });
  };

  const reorderLists = async (orderedIds) => {
    // Optimistic local update
    const byId = Object.fromEntries(lists.map((l) => [l.id, l]));
    const optimistic = orderedIds.map((id, i) => ({ ...byId[id], order: i })).filter(Boolean);
    setLists(optimistic);
    try {
      const { data } = await axios.post(`${API}/lists/reorder`, { ordered_ids: orderedIds });
      setLists(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save list order");
      await fetchTasks(); // rollback to server truth
    }
  };

  const confirmDeleteList = async ({ list, mode, targetListId }) => {
    try {
      let url = `${API}/lists/${list.id}`;
      const params = new URLSearchParams();
      if (mode === "move") params.set("move_to_list_id", targetListId);
      if (mode === "delete_tasks") params.set("delete_tasks", "true");
      if ([...params].length) url += `?${params}`;
      await axios.delete(url);
      setLists((prev) => prev.filter((l) => l.id !== list.id));
      if (filter === `list:${list.id}`) setFilter("all");
      await fetchTasks();
      toast.success(`Deleted "${list.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Couldn't delete list");
    }
  };

  const isCompletedView = filter === "completed";
  const activeListId = filter.startsWith("list:") ? filter.slice(5) : null;
  const activeList = activeListId ? lists.find((l) => l.id === activeListId) : null;
  const activeListPerm = activeList?.permission;
  // For tasks in cross-list views, derive each task's permission from its list_id.
  const listsById = Object.fromEntries(lists.map((l) => [l.id, l]));
  const isReadOnlyForTask = (task) => {
    const tl = listsById[task.list_id];
    return tl && tl.permission === "view";
  };
  const isReadOnlyForActiveList = activeListPerm === "view";
  const isSharedActiveList = activeList && activeList.share_origin && activeList.share_origin !== "owner";

  // ---- Per-list sort + filter helpers ----
  const DEFAULT_PREFS = { sort: "order", priorityFilter: "all", dueFilter: "all", view: "list" };
  const getListPref = (lid) => ({ ...DEFAULT_PREFS, ...(listPrefs[lid] || {}) });
  const setListPref = (lid, patch) =>
    setListPrefs((prev) => ({ ...prev, [lid]: { ...DEFAULT_PREFS, ...(prev[lid] || {}), ...patch } }));

  const applyListPrefs = (rows, lid) => {
    if (!lid) return rows;
    const p = getListPref(lid);
    let out = [...rows];
    // Priority filter
    if (p.priorityFilter !== "all") {
      out = out.filter((t) => (t.priority || "medium") === p.priorityFilter);
    }
    // Due date filter
    if (p.dueFilter !== "all") {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const todayIso = today.toISOString().slice(0, 10);
      const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7);
      const weekEndIso = weekEnd.toISOString().slice(0, 10);
      out = out.filter((t) => {
        if (p.dueFilter === "none") return !t.due_date;
        if (!t.due_date) return false;
        if (p.dueFilter === "overdue") return t.due_date < todayIso && !t.completed;
        if (p.dueFilter === "today") return t.due_date === todayIso;
        if (p.dueFilter === "week") return t.due_date >= todayIso && t.due_date <= weekEndIso;
        return true;
      });
    }
    // Sort
    const prioRank = { high: 0, medium: 1, low: 2 };
    out.sort((a, b) => {
      if (p.sort === "priority") return prioRank[a.priority || "medium"] - prioRank[b.priority || "medium"];
      if (p.sort === "due_date") return (a.due_date || "9999-12-31").localeCompare(b.due_date || "9999-12-31");
      if (p.sort === "title") return (a.title || "").localeCompare(b.title || "");
      if (p.sort === "created_at") return (b.created_at || "").localeCompare(a.created_at || "");
      return (a.order || 0) - (b.order || 0);
    });
    return out;
  };

  const filteredTasks = () => {
    if (isCompletedView) return completedTasks;
    if (activeListId) return applyListPrefs(tasks.filter((t) => t.list_id === activeListId), activeListId);
    return tasks;
  };

  const focusTask = focusTaskId
    ? tasks.find((t) => t.id === focusTaskId) || completedTasks.find((t) => t.id === focusTaskId)
    : null;

  // Keyboard shortcuts: /, b, space, Esc
  useEffect(() => {
    const handler = (e) => {
      // Esc always closes focus mode
      if (e.key === "Escape" && focusTaskId) {
        e.preventDefault();
        setFocusTaskId(null);
        return;
      }
      if (isTyping()) return;

      if (e.key === "/") {
        e.preventDefault();
        const input = document.querySelector('[data-testid="task-title-input"]');
        if (input) input.focus();
        return;
      }

      if (e.key === "b" || e.key === "B") {
        e.preventDefault();
        if (focusTask) {
          breakdown(focusTask.id);
        } else {
          const candidate = tasks.find((tk) => !tk.subtasks?.length && !tk.completed);
          if (candidate) {
            breakdown(candidate.id);
            toast.info(`Breaking down: ${candidate.title}`);
          } else {
            toast.info("No task to break down. Add one or open one in Focus mode.");
          }
        }
        return;
      }

      if (e.key === " " || e.code === "Space") {
        // Toggle timer on focus task or first running/first-with-timer task
        const target =
          focusTask ||
          tasks.find((tk) => tk.timer_running) ||
          tasks.find((tk) => tk.duration_minutes);
        if (!target || !target.duration_minutes) return;
        e.preventDefault();
        if (target.timer_running) {
          // Pause
          const stored = target.timer_remaining_seconds ?? target.duration_minutes * 60;
          const elapsed = target.timer_started_at
            ? Math.floor((Date.now() - new Date(target.timer_started_at).getTime()) / 1000)
            : 0;
          updateTask(target.id, {
            timer_running: false,
            timer_started_at: null,
            timer_remaining_seconds: Math.max(0, stored - elapsed),
          });
        } else {
          updateTask(target.id, {
            timer_running: true,
            timer_started_at: new Date().toISOString(),
          });
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTaskId, tasks, focusTask]);

  const visible = filteredTasks();
  const counts = {
    all: tasks.length,
    completed: completedTasks.length,
  };
  lists.forEach((l) => {
    counts[`list:${l.id}`] = tasks.filter((t) => t.list_id === l.id).length;
  });

  const titles = {
    all: { kicker: "All tasks", heading: "Today's focus" },
    completed: { kicker: "Archive", heading: "What you've crushed." },
  };
  const t = activeList
    ? { kicker: activeList.category === "work" ? "Work · " + activeList.name : "Personal · " + activeList.name, heading: activeList.name }
    : titles[filter] || titles.all;

  return (
    <div className="App min-h-screen bg-white flex flex-col">
      <Header user={user} onLogout={logout} onOpenSettings={() => setSettingsOpen(true)} onOpenMenu={() => setMobileMenuOpen(true)} billing={billing} />
      <div className="flex flex-col lg:flex-row max-w-6xl mx-auto w-full flex-1">
        <div className="hidden lg:block">
          <Sidebar
            filter={filter}
            setFilter={setFilter}
            counts={counts}
            lists={lists}
            onCreateList={handleRequestCreateList}
            onUpdateList={updateList}
            onDeleteList={requestDeleteList}
            onReorderLists={reorderLists}
            onShareList={(list) => setSharingListId(list.id)}
            onShareWorkspace={() => setWorkspaceShareOpen(true)}
          />
        </div>
        <main className="flex-1 p-6 md:p-10 lg:border-l border-zinc-200 min-h-[calc(100vh-64px)]">
          <PlanBanner billing={billing} />
          <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">
                {t.kicker}
              </p>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-none text-zinc-900">
                {t.heading}
              </h1>
              <p className="text-sm text-zinc-500 mt-3" data-testid="task-counter">
                {isCompletedView
                  ? `${visible.length} ${visible.length === 1 ? "task" : "tasks"} completed`
                  : `${visible.length} ${visible.length === 1 ? "task" : "tasks"} remaining`}
              </p>
            </div>
            {isCompletedView && completedTasks.length > 0 && (
              <button
                type="button"
                onClick={clearArchive}
                className="text-xs uppercase tracking-[0.12em] font-semibold px-4 py-2.5 border border-red-300 text-red-700 hover:bg-red-50 hover:border-red-500 transition-colors"
                data-testid="clear-archive-btn"
              >
                Clear archive
              </button>
            )}
          </div>

          {isSharedActiveList && (
            <div className={`mb-4 border px-4 py-3 flex items-center justify-between gap-3 flex-wrap ${isReadOnlyForActiveList ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="shared-list-banner">
              <p className="text-sm">
                <span className="font-semibold">{activeList.owner_name || activeList.owner_email}</span> shared this list with you ·{" "}
                <span className={`text-[10px] uppercase tracking-[0.12em] font-medium px-1.5 py-0.5 border ${isReadOnlyForActiveList ? "border-amber-700 text-amber-900" : "border-emerald-700 text-emerald-900"}`}>
                  {activeListPerm}
                </span>
              </p>
            </div>
          )}

          {!isCompletedView && !isReadOnlyForActiveList && (
            <AddTaskForm
              onAdd={addTask}
              lists={lists.filter((l) => l.permission !== "view")}
              defaultListId={activeListId || null}
              defaultDurationMinutes={user?.default_duration_minutes || null}
            />
          )}

          {/* Top picks aggregated across all lists — shown on the "All" tab */}
          {!isCompletedView && !activeListId && filter === "all" && (
            <AllTopPicks
              tasks={tasks}
              lists={lists}
              onOpenTask={(task) => setEditingTaskId(task.id)}
              onPickList={(listId) => setFilter(`list:${listId}`)}
            />
          )}

          {/* Top picks + per-list toolbar — only when a specific list is selected */}
          {!isCompletedView && activeListId && (
            <>
              <TopPicksBar
                listId={activeListId}
                readOnly={isReadOnlyForActiveList}
                tasks={[...tasks, ...completedTasks].filter((t) => t.list_id === activeListId)}
                onOpenTask={(task) => setEditingTaskId(task.id)}
                onTaskChanged={fetchTasks}
              />
              <ListToolbar
                sort={getListPref(activeListId).sort}
                setSort={(v) => setListPref(activeListId, { sort: v })}
                priorityFilter={getListPref(activeListId).priorityFilter}
                setPriorityFilter={(v) => setListPref(activeListId, { priorityFilter: v })}
                dueFilter={getListPref(activeListId).dueFilter}
                setDueFilter={(v) => setListPref(activeListId, { dueFilter: v })}
                view={getListPref(activeListId).view}
                setView={(v) => setListPref(activeListId, { view: v })}
                onExport={() => setExportingList(activeList)}
                canExport
              />
            </>
          )}

          {isCompletedView && completedTasks.length > 0 && (
            <div className="mt-2 mb-2 border border-zinc-200 bg-zinc-50 px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="completed-share-banner">
              <p className="text-sm text-zinc-700">
                <span className="font-medium text-zinc-900">Nice work.</span> Help a friend break their own list down →
              </p>
              <ShareAppLink
                medium="completed"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] font-medium px-3 py-2 bg-zinc-900 hover:bg-[#002FA7] text-white whitespace-nowrap transition-colors"
                dataTestId="completed-share-btn"
              >
                Share BreakItDown
              </ShareAppLink>
            </div>
          )}

          <div className="mt-10">
            {loading ? (
              <div className="text-sm text-zinc-400" data-testid="loading-state">Loading...</div>
            ) : visible.length === 0 ? (
              <EmptyState
                title={isCompletedView ? "No archive yet." : "Nothing on the list."}
                subtitle={
                  isCompletedView
                    ? "Tasks you complete will appear here. Get started!"
                    : "Add your first big task above — we'll help break it down into bite-sized steps."
                }
              />
            ) : isCompletedView ? (
              <ul className="border-t border-zinc-200" data-testid="task-list">
                {visible.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    isCompletedView
                    onToggle={() => toggleTask(task)}
                    onDelete={() => deleteTask(task.id)}
                    onAddSubtask={() => {}}
                    onToggleSubtask={() => {}}
                    onDeleteSubtask={() => {}}
                    onBreakdown={() => {}}
                    onUpdate={() => {}}
                  />
                ))}
              </ul>
            ) : activeListId && getListPref(activeListId).view === "timeline" ? (
              <TaskTimeline tasks={visible} onOpenTask={(t) => setEditingTaskId(t.id)} />
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={visible.map((tk) => tk.id)} strategy={verticalListSortingStrategy}>
                  <ul className="border-t border-zinc-200" data-testid="task-list">
                    {visible.map((task) => (
                      <TaskItem
                        key={task.id}
                        task={task}
                        isRemoving={removingIds.has(task.id)}
                        readOnly={isReadOnlyForTask(task)}
                        onToggle={() => toggleTask(task)}
                        onDelete={() => deleteTask(task.id)}
                        onAddSubtask={(title) => addSubtask(task.id, title)}
                        onToggleSubtask={(sid, c) => toggleSubtask(task.id, sid, c)}
                        onDeleteSubtask={(sid) => deleteSubtask(task.id, sid)}
                        onReorderSubtasks={(ids) => reorderSubtasks(task.id, ids)}
                        onBreakdown={() => breakdown(task.id)}
                        onUpdate={(u) => updateTask(task.id, u)}
                        onFocus={() => setFocusTaskId(task.id)}
                        onEdit={() => setEditingTaskId(task.id)}
                        onShare={() => setSharingTaskId(task.id)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </main>
      </div>
      <Footer />
      {focusTask && (
        <FocusMode
          task={focusTask}
          onClose={() => setFocusTaskId(null)}
          onUpdate={(u) => updateTask(focusTask.id, u)}
          onToggleSubtask={(sid, c) => toggleSubtask(focusTask.id, sid, c)}
          onComplete={() => toggleTask(focusTask)}
          onBreakdown={() => breakdown(focusTask.id)}
        />
      )}
      {editingTaskId && (
        <EditTaskDialog
          task={tasks.find((tk) => tk.id === editingTaskId)}
          open={!!editingTaskId}
          onClose={() => setEditingTaskId(null)}
          onSave={(updates) => updateTask(editingTaskId, updates)}
          onBreakdown={() => breakdown(editingTaskId)}
        />
      )}
      {sharingTaskId && (
        <ShareDialog
          task={tasks.find((tk) => tk.id === sharingTaskId)}
          open={!!sharingTaskId}
          onClose={() => setSharingTaskId(null)}
          onChange={(token) =>
            setTasks((ts) =>
              ts.map((tk) =>
                tk.id === sharingTaskId
                  ? token
                    ? { ...tk, share_token: token }
                    : (() => {
                        const { share_token: _ignored, ...rest } = tk;
                        return rest;
                      })()
                  : tk
              )
            )
          }
        />
      )}
      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <CreateListDialog
        open={createListOpen}
        onClose={() => setCreateListOpen(false)}
        onCreate={createList}
      />
      <DeleteListDialog
        open={!!deleteListTarget}
        list={deleteListTarget?.list}
        taskCount={deleteListTarget?.taskCount || 0}
        otherLists={lists.filter((l) => l.id !== deleteListTarget?.list?.id)}
        onConfirm={confirmDeleteList}
        onClose={() => setDeleteListTarget(null)}
      />
      <UpgradeModal
        open={!!upgradeGate}
        feature={upgradeGate?.feature}
        message={upgradeGate?.message}
        onClose={() => setUpgradeGate(null)}
      />
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        billing={billing}
        filter={filter}
        setFilter={setFilter}
        counts={counts}
        lists={lists}
        onCreateList={handleRequestCreateList}
        onUpdateList={updateList}
        onDeleteList={requestDeleteList}
        onReorderLists={reorderLists}
        onShareList={(list) => setSharingListId(list.id)}
        onShareWorkspace={() => setWorkspaceShareOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
        onLogout={logout}
      />
      <ShareListDialog
        list={lists.find((l) => l.id === sharingListId)}
        open={!!sharingListId}
        onClose={() => setSharingListId(null)}
        onPaywall={(gate) => setUpgradeGate(gate)}
      />
      <ShareWorkspaceDialog
        open={workspaceShareOpen}
        onClose={() => setWorkspaceShareOpen(false)}
        onPaywall={(gate) => setUpgradeGate(gate)}
      />
      <ExportDialog
        list={exportingList}
        open={!!exportingList}
        onClose={() => setExportingList(null)}
      />
    </div>
  );
}

function PlanBanner({ billing }) {
  if (!billing || billing.grandfathered) return null;
  const { plan, days_remaining, list_count, list_limit } = billing;

  // Free user at/over cap: prominent upgrade nudge
  if (plan === "free" && list_limit != null && list_count >= list_limit) {
    return (
      <div className="mb-6 border border-amber-300 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="plan-banner-cap">
        <p className="text-sm text-amber-900">
          <span className="font-semibold">You've hit the Free list cap</span> ({list_count}/{list_limit}). Upgrade to Pro for unlimited lists and collaboration.
        </p>
        <Link to="/billing" className="text-xs font-medium uppercase tracking-[0.12em] px-4 py-2 bg-zinc-900 hover:bg-[#002FA7] text-white transition-colors whitespace-nowrap" data-testid="plan-banner-upgrade">
          See Pro plans →
        </Link>
      </div>
    );
  }
  // Trial expiring soon
  if (plan === "trialing" && days_remaining != null && days_remaining <= 7) {
    return (
      <div className="mb-6 border border-zinc-900 bg-zinc-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="plan-banner-trial">
        <p className="text-sm text-zinc-900">
          <span className="font-semibold">Your trial ends in {days_remaining} day{days_remaining === 1 ? "" : "s"}.</span> Upgrade to keep Pro features.
        </p>
        <Link to="/billing" className="text-xs font-medium uppercase tracking-[0.12em] px-4 py-2 bg-[#002FA7] hover:bg-zinc-900 text-white transition-colors whitespace-nowrap" data-testid="plan-banner-trial-cta">
          Choose a plan →
        </Link>
      </div>
    );
  }
  // Pro expiring soon (renewal reminder for one-time-checkout model)
  if (plan === "pro" && days_remaining != null && days_remaining <= 14) {
    return (
      <div className="mb-6 border border-zinc-300 bg-zinc-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="plan-banner-renewal">
        <p className="text-sm text-zinc-700">
          <span className="font-semibold">Pro renews in {days_remaining} day{days_remaining === 1 ? "" : "s"}.</span> Renew now to keep your lists unlocked.
        </p>
        <Link to="/billing" className="text-xs font-medium uppercase tracking-[0.12em] px-4 py-2 border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors whitespace-nowrap" data-testid="plan-banner-renew">
          Renew Pro →
        </Link>
      </div>
    );
  }
  return null;
}

